import React, { memo, useEffect, useState } from 'react';
import { useGetAnnotationsQuery, useGetShapeAnnotations } from './useAnnotationHooks';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useQueryClient } from 'react-query';
import { SVGAnnotationRenderer } from './SVGAnnotationRenderer';
import { AnnotationInteractionHandler } from './AnnotationInteractionHandler';
import { useAnnotationStore } from './annotationStore';
import { useFetchObjects } from '../../hooks/objectHooks';
import { useFetchUser } from '../../hooks/useFetchUsers';

import { PastePreview } from './PastePreview';
import { AnnotationType } from '../../api/protosCompiled/annotation/annotation_pb';
import { useUpdateShape, wouldGoOutOfBounds } from './utils';

// Use memo to prevent unnecessary re-renders
const SVGAnnotations: React.FC = memo(() => {
  const selectedObject = useStore(useUnityBuildStore).selectedObject;
  const queryClient = useQueryClient();
  const [previousObjectId, setPreviousObjectId] = useState<string | null>(null);
  const { data: availableObjects } = useFetchObjects();
  const { data: currentUser } = useFetchUser();
  const updateShape = useUpdateShape();
  // Get annotation state and actions
  const {
    optimisticShapes,
    selectedShapeId,
    selectedShapeIds,
    setSelectedShapeId,
    removeOptimisticShape,
    annotationVisibilityMode,
    cutOriginalAnnotation,
    setLiveEditingShape,
    setCoordinatedDragState,
  } = useAnnotationStore();

  // Force cache clearing and refetch when object or estimate changes
  useEffect(() => {
    const currObjectId = selectedObject?.id || '';

    // Only run when a change is detected
    if (previousObjectId !== null && previousObjectId !== currObjectId) {
      // Clear all annotation queries
      queryClient.removeQueries(['annotations', currObjectId]);
    }

    // Update previous values for next comparison
    setPreviousObjectId(currObjectId);
  }, [selectedObject?.id, queryClient, previousObjectId]);

  const { isLoading, isError } = useGetAnnotationsQuery(selectedObject?.id || '');

  // Fetch shape annotations (non-text) with full geometry/type
  const {
    annotations: shapeAnno,
    isLoading: isLoadingShapes,
    isError: isErrorShapes,
  } = useGetShapeAnnotations(selectedObject?.id || '');

  // Clean up optimistic shapes when real shapes arrive
  useEffect(() => {
    if (shapeAnno.length > 0) {
      const realShapeIds = new Set(shapeAnno.map(a => a.id));
      optimisticShapes.forEach(optimisticShape => {
        if (realShapeIds.has(optimisticShape.id)) {
          removeOptimisticShape(optimisticShape.id);
        }
      });
    }
  }, [shapeAnno, optimisticShapes, removeOptimisticShape]);

  // Listen for drag-select rectangles from the mouseEvents system and update selection live
  useEffect(() => {
    const handleRect = (e: any) => {
      const rect = e?.detail?.rect;
      if (!rect) return;
      // Compute which annotations fall within the rect and mark selected in store
      const ids: string[] = [];
      const withinRect = (x: number, y: number) =>
        x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

      const inBounds = (ann: any): boolean => {
        const g = ann.geometry || {};
        const t: AnnotationType = ann.type;
        switch (t) {
          case AnnotationType.RECTANGLE:
          case AnnotationType.TEXT:
          case AnnotationType.HIGHLIGHTER: {
            const x = parseFloat(g.x || '0');
            const y = parseFloat(g.y || '0');
            const w = parseFloat(g.width || '0');
            const h = parseFloat(g.height || '0');
            const cx = x + w / 2;
            const cy = y + h / 2;
            return withinRect(cx, cy);
          }
          case AnnotationType.CIRCLE:
          case AnnotationType.ELLIPSE: {
            const cx = parseFloat(g.cx || '0');
            const cy = parseFloat(g.cy || '0');
            return withinRect(cx, cy);
          }
          case AnnotationType.LINE:
          case AnnotationType.ARROW: {
            const x1 = parseFloat(g.x1 || '0');
            const y1 = parseFloat(g.y1 || '0');
            const x2 = parseFloat(g.x2 || '0');
            const y2 = parseFloat(g.y2 || '0');
            const cx = (x1 + x2) / 2;
            const cy = (y1 + y2) / 2;
            return withinRect(cx, cy);
          }
          case AnnotationType.POLYLINE:
          case AnnotationType.POLYGON: {
            const points: string = g.points || '';
            if (!points) return false;
            const coords = points
              .split(' ')
              .filter(Boolean)
              .map(p => p.split(',').map(Number));
            const xs = coords.map(([x]) => x);
            const ys = coords.map(([, y]) => y);
            const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
            const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
            return withinRect(cx, cy);
          }
          default:
            return false;
        }
      };

      shapeAnno.forEach(ann => {
        if (inBounds(ann)) ids.push(ann.id);
      });

      const store = require('./annotationStore').useAnnotationStore.getState();
      store.clearSelectedShapeIds?.();
      ids.forEach(id => store.addSelectedShapeId?.(id));
    };

    const handleEnd = () => {
      // no-op; selection already applied live
    };

    document.addEventListener('annotations:drag-select-rect', handleRect as EventListener);
    document.addEventListener('annotations:drag-select-end', handleEnd as EventListener);
    return () => {
      document.removeEventListener('annotations:drag-select-rect', handleRect as EventListener);
      document.removeEventListener('annotations:drag-select-end', handleEnd as EventListener);
    };
  }, [shapeAnno]);

  // Listen for coordinated drag events to move selected annotations with takeoff points
  useEffect(() => {
    const handleCoordinatedDragStart = (e: CustomEvent) => {
      const { mode } = e.detail;
      if (mode === 'group' && selectedShapeIds.length > 0) {
        // Reset collision state
        hasCollision = false;

        // Add collision event listener
        document.addEventListener('annotation:border-collision', handleCollision as EventListener);

        // Store initial positions of selected annotations for coordinated dragging
        const initialPositions = new Map();
        shapeAnno.forEach(annotation => {
          if (selectedShapeIds.includes(annotation.id)) {
            initialPositions.set(annotation.id, {
              x: parseFloat(annotation.geometry?.x || '0'),
              y: parseFloat(annotation.geometry?.y || '0'),
              cx: parseFloat(annotation.geometry?.cx || '0'),
              cy: parseFloat(annotation.geometry?.cy || '0'),
              x1: parseFloat(annotation.geometry?.x1 || '0'),
              y1: parseFloat(annotation.geometry?.y1 || '0'),
              x2: parseFloat(annotation.geometry?.x2 || '0'),
              y2: parseFloat(annotation.geometry?.y2 || '0'),
            });
          }
        });
        (window as any).__annotationInitialPositions = initialPositions;

        // Activate coordinated drag state
        setCoordinatedDragState(true, new Map(), new Map());
      }
    };

    // Track collision state for coordinated dragging
    let hasCollision = false;

    const handleCollision = (e: CustomEvent) => {
      hasCollision = true;
    };

    const handleCoordinatedDragUpdate = (e: CustomEvent) => {
      const { offset } = e.detail;

      if (selectedShapeIds.length > 0) {
        // Get PDF bounds from the store for constraining
        const unityStore = (require('../../stores/store') as any).useUnityBuildStore?.getState?.();
        const pdfBounds = unityStore?.pdfDimensions;

        // Check if ANY annotation would go out of bounds with this movement
        if (pdfBounds) {
          const wouldCollide = selectedShapeIds.some(annotationId => {
            const ann = shapeAnno.find(a => a.id === annotationId);
            if (!ann) return false;
            return wouldGoOutOfBounds(ann.geometry || {}, ann.type, offset.x, offset.y, pdfBounds);
          });

          if (wouldCollide) {
            // Dispatch collision event to stop all drag operations
            document.dispatchEvent(
              new CustomEvent('annotation:border-collision', {
                detail: {
                  groupCollision: true,
                  bounds: pdfBounds,
                  movement: { dx: offset.x, dy: offset.y },
                  timestamp: Date.now(),
                },
              })
            );
            hasCollision = true;
            return;
          }
        }

        // Check if we have a collision from another source - if so, don't move
        if (hasCollision) {
          return;
        }

        // Build live geometry by offsetting original annotation geometries per-type
        const liveEditingGeometry = new Map();
        const liveEditingTransform = new Map();

        selectedShapeIds.forEach(annotationId => {
          const ann = shapeAnno.find(a => a.id === annotationId);
          if (!ann) return;
          // Clamp group live geometry to bounds to prevent escaping
          const newGeom = (require('./utils') as any).moveShapeByDelta(
            offset.x,
            offset.y,
            ann.geometry || {},
            ann.type,
            pdfBounds
          );
          liveEditingGeometry.set(annotationId, newGeom);
        });

        setCoordinatedDragState(true, liveEditingGeometry, liveEditingTransform);
      }
    };

    const handleCoordinatedDragEnd = (e: CustomEvent) => {
      if (selectedShapeIds.length > 0) {
        try {
          // Remove collision event listener
          document.removeEventListener(
            'annotation:border-collision',
            handleCollision as EventListener
          );

          // Reset collision state
          hasCollision = false;

          // Get coordinated live geometry for exact positions
          const store = (require('./annotationStore') as any).useAnnotationStore?.getState?.();
          const coordGeom: Map<string, any> | undefined = store?.coordinatedDragGeometry;
          const liveEditingShapeId: string | null = store?.liveEditingShapeId || null;
          const liveEditingGeometry: any = store?.liveEditingGeometry || null;

          // Get event details for the primary dragged annotation
          const { selectedIds, primaryDraggedId } = e.detail || {};

          // Use event selectedIds if available (includes primary dragged annotation), otherwise use store
          const idsToSave =
            Array.isArray(selectedIds) && selectedIds.length > 0 ? selectedIds : selectedShapeIds;

          // Ensure primary dragged annotation is always included
          const allIdsToSave =
            primaryDraggedId && !idsToSave.includes(primaryDraggedId)
              ? [...idsToSave, primaryDraggedId]
              : idsToSave;

          allIdsToSave.forEach(annotationId => {
            const annotation = shapeAnno.find(a => a.id === annotationId);
            if (!annotation) return;

            // Get the EXACT geometry from what's currently displayed on screen
            // Prefer coordinated map; fallback to live editing geometry for the primary dragged shape; then to persisted geometry
            const fromCoord = coordGeom?.get(annotationId);
            const fromLive = liveEditingShapeId === annotationId ? liveEditingGeometry : null;
            const exactGeometry = fromCoord || fromLive || annotation.geometry;

            // Round coordinates and save EXACTLY what's on screen
            const roundedGeometry = (require('./utils') as any).roundGeometryCoordinates(
              exactGeometry
            );

            updateShape(annotation.id, {
              id: annotation.id,
              objectId: annotation.objectId,
              type: annotation.type,
              geometry: roundedGeometry,
              style: annotation.style,
              transform: annotation.transform,
              layerId: (annotation as any).layerId || 'default',
              zIndex: (annotation as any).zIndex ?? 0,
              isdimensional: annotation.isdimensional,
            });
          });
        } catch (err) {
          console.error('Failed to persist coordinated drag end for annotations:', err);
        }
      }

      // Clear selections after coordinated drag to prevent stuck selection state
      setTimeout(() => {
        const store = (require('./annotationStore') as any).useAnnotationStore?.getState?.();
        store?.clearSelectedShapeIds?.();

        // Also clear current takeoff assembly
        const assemblyStore = (
          require('../../stores/AssemblyStore') as any
        ).useAssemblyStore?.getState?.();
        assemblyStore?.setCurrentTakeoffAssembly?.(null);
      }, 50);

      // Clean up
      (window as any).__annotationInitialPositions = null;
      setCoordinatedDragState(false, new Map(), new Map());
    };

    document.addEventListener(
      'coordinated:drag-start',
      handleCoordinatedDragStart as EventListener
    );
    document.addEventListener(
      'coordinated:drag-update',
      handleCoordinatedDragUpdate as EventListener
    );
    document.addEventListener('coordinated:drag-end', handleCoordinatedDragEnd as EventListener);

    // Fallback: finalize coordinated drag if an external handler (e.g., Takeoff Assembly)
    // consumed the drag-end and didn't emit our event. Persist using coordinated live geometry.
    const handleGlobalMouseUp = () => {
      try {
        const store = (require('./annotationStore') as any).useAnnotationStore?.getState?.();
        const active = !!store?.coordinatedDragActive;
        const coordGeom: Map<string, any> | undefined = store?.coordinatedDragGeometry;
        if (!active || !coordGeom || coordGeom.size === 0) return;

        const idsToSave =
          selectedShapeIds.length > 0 ? selectedShapeIds : Array.from(coordGeom.keys());

        idsToSave.forEach((annotationId: string) => {
          const annotation = shapeAnno.find(a => a.id === annotationId);
          if (!annotation) return;
          const finalGeometry = coordGeom.get(annotationId) || annotation.geometry;
          const roundedGeometry = (require('./utils') as any).roundGeometryCoordinates(
            finalGeometry
          );
          updateShape(annotation.id, {
            id: annotation.id,
            objectId: annotation.objectId,
            type: annotation.type,
            geometry: roundedGeometry,
            style: annotation.style,
            transform: annotation.transform,
            layerId: (annotation as any).layerId || 'default',
            zIndex: (annotation as any).zIndex ?? 0,
            isdimensional: annotation.isdimensional,
          });
        });

        setCoordinatedDragState(false, new Map(), new Map());
      } catch {}
    };

    document.addEventListener('mouseup', handleGlobalMouseUp as EventListener);

    return () => {
      document.removeEventListener(
        'coordinated:drag-start',
        handleCoordinatedDragStart as EventListener
      );
      document.removeEventListener(
        'coordinated:drag-update',
        handleCoordinatedDragUpdate as EventListener
      );
      document.removeEventListener(
        'coordinated:drag-end',
        handleCoordinatedDragEnd as EventListener
      );
      document.removeEventListener('mouseup', handleGlobalMouseUp as EventListener);
    };
  }, [selectedShapeIds, shapeAnno, setLiveEditingShape, setCoordinatedDragState, updateShape]);

  // Note: Escape key handling for clearing shape selection is now handled globally in ToolBar.tsx

  // Handle shape selection
  const handleShapeSelect = (annotation: any) => {
    setSelectedShapeId(annotation.id);
  };

  // Exclude any annotation IDs that are shapes so they don't render as notes
  const shapeIdSet = new Set(shapeAnno.map(a => a.id));

  // Filter optimistic shapes to exclude any that have real counterparts
  // and only show shapes for the current selected object
  const activeOptimisticShapes = optimisticShapes.filter(
    optimisticShape =>
      !shapeIdSet.has(optimisticShape.id) && optimisticShape.objectId === (selectedObject?.id || '')
  );

  // Annotation editor is now handled in MasterProjectView.tsx
  // Get target object name for tooltips
  const getTargetObjectName = (targetObjectId: string) => {
    if (!targetObjectId || !availableObjects) return 'Unknown destination';
    const targetObject = availableObjects.find(obj => obj.id === targetObjectId);
    return targetObject?.objectname || 'Unknown destination';
  };

  // Handle hyperlink navigation: dispatch event for toolbar to switch documents
  const handleHyperlinkClick = (targetObjectId: string) => {
    const ubsStore = useUnityBuildStore.getState();
    const selected = ubsStore.selectedObject;
    if (selected && selected.id === targetObjectId) return;
    document.dispatchEvent(
      new CustomEvent('app:navigateToObject', { detail: { objectId: targetObjectId } })
    );
  };

  if (isLoading || isLoadingShapes) {
    return null; // Or return a loading indicator if preferred
  }

  if (isError || isErrorShapes) {
    return null; // Or return an error message if preferred
  }

  // If annotations are hidden, only render the interaction handler for creating new ones
  if (annotationVisibilityMode === 'none') {
    return (
      <AnnotationInteractionHandler>
        <g className="annotations-container" style={{ zIndex: 1 }}>
          {/* Text annotations are now created through AnnotationInteractionHandler */}
        </g>
      </AnnotationInteractionHandler>
    );
  }

  return (
    <>
      <AnnotationInteractionHandler>
        {/* Render shape annotations */}
        <g className="annotations">
          {shapeAnno
            .filter(annotation => {
              // Hide cut items during paste preview
              if (cutOriginalAnnotation && cutOriginalAnnotation.id === annotation.id) {
                return false;
              }

              // Filter by annotation visibility mode
              if (annotationVisibilityMode === 'mine') {
                // Only show annotations created by the current user
                return annotation.createdByUserId === currentUser?.user?.id;
              }

              return true;
            })
            .map(annotation => (
              <SVGAnnotationRenderer
                key={annotation.id}
                annotation={annotation as any}
                onHyperlinkClick={handleHyperlinkClick}
                onSelect={handleShapeSelect}
                isSelected={
                  selectedShapeId === annotation.id ||
                  (selectedShapeIds || []).includes(annotation.id)
                }
                getTargetObjectName={getTargetObjectName}
              />
            ))}
          {/* Render optimistic shapes (only those without real counterparts) */}
          {activeOptimisticShapes
            .filter(shape => {
              // Hide cut items during paste preview
              if (cutOriginalAnnotation && cutOriginalAnnotation.id === shape.id) {
                return false;
              }

              // Filter by annotation visibility mode
              if (annotationVisibilityMode === 'mine') {
                // Optimistic shapes are always created by the current user, so show them
                return true;
              }

              return true;
            })
            .map((shape: any) => (
              <SVGAnnotationRenderer
                key={shape.id}
                annotation={{
                  ...shape,
                  id: shape.id,
                  objectId: shape.objectId,
                  createdByUserId: '',
                  createdByAccountId: '',
                  lastUpdated: new Date().toISOString(),
                  type: shape.type,
                  geometry: shape.geometry,
                  style: shape.style,
                }}
                onHyperlinkClick={handleHyperlinkClick}
                onSelect={handleShapeSelect}
                isSelected={
                  selectedShapeId === shape.id || (selectedShapeIds || []).includes(shape.id)
                }
                getTargetObjectName={getTargetObjectName}
              />
            ))}
        </g>

        {/* Text annotations are now created through AnnotationInteractionHandler */}
      </AnnotationInteractionHandler>

      {/* Paste Preview - rendered within SVG layer */}
      <PastePreview />

      {/* Annotation Editor is rendered in MasterProjectView.tsx */}
    </>
  );
});

// Export as named and default export
export { SVGAnnotations };
export default SVGAnnotations;
